import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import DashboardLayout from '../components/DashboardLayout';
import {
  ArrowLeftIcon,
  CalendarIcon,
  UserIcon,
  DocumentTextIcon,
  ClipboardDocumentCheckIcon,
  ExclamationCircleIcon,
  PhotoIcon,
  XMarkIcon,
} from '@heroicons/react/24/outline';
import { examsService, filesService } from '../services/api';

const ExamDetails = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [exam, setExam] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [examFiles, setExamFiles] = useState([]);
  const [showImageModal, setShowImageModal] = useState(false);
  const [selectedImage, setSelectedImage] = useState(null);

  useEffect(() => {
    loadExamDetails();
    loadExamFiles();
  }, [id]);

  const loadExamDetails = async () => {
    try {
      const data = await examsService.getById(id);
      setExam(data);
    } catch (err) {
      console.error('Erro ao carregar exame:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const loadExamFiles = async () => {
    try {
      const files = await examsService.getFiles(id);
      setExamFiles(files);
    } catch (err) {
      console.error('Erro ao carregar arquivos do exame:', err);
    }
  };

  const openImageModal = (imageUrl) => {
    setSelectedImage(imageUrl);
    setShowImageModal(true);
  };

  const closeImageModal = () => {
    setShowImageModal(false);
    setSelectedImage(null);
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleDateString('pt-BR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
    });
  };

  const formatDateTime = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleString('pt-BR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const formatCRM = (crm, crmState) => {
    if (!crm) return null;
    return crmState ? `${crm}-${crmState}` : crm;
  };

  const getSexLabel = (sex) => {
    if (sex === 'M') return 'Masculino';
    if (sex === 'F') return 'Feminino';
    return 'Não informado';
  };

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center min-h-screen">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
        </div>
      </DashboardLayout>
    );
  }

  if (error || !exam) {
    return (
      <DashboardLayout>
        <div className="p-8">
          <div className="max-w-4xl mx-auto">
            <div className="bg-red-50 border border-red-200 rounded-lg p-8 text-center">
              <ExclamationCircleIcon className="w-16 h-16 text-red-500 mx-auto mb-4" />
              <h2 className="text-2xl font-bold text-red-900 mb-2">Erro ao Carregar Exame</h2>
              <p className="text-red-700 mb-6">{error || 'Exame não encontrado'}</p>
              <button
                onClick={() => navigate('/exams')}
                className="inline-flex items-center gap-2 px-6 py-3 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors"
              >
                <ArrowLeftIcon className="w-5 h-5" />
                Voltar para Lista de Exames
              </button>
            </div>
          </div>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="p-8">
        <div className="max-w-6xl mx-auto">
          {/* Header */}
          <div className="mb-8">
            <button
              onClick={() => navigate('/exams')}
              className="inline-flex items-center gap-2 text-gray-600 hover:text-gray-900 mb-4 transition-colors"
            >
              <ArrowLeftIcon className="w-5 h-5" />
              Voltar para Exames
            </button>
            <div className="flex items-center gap-3 mb-2">
              <DocumentTextIcon className="w-8 h-8 text-blue-600" />
              <h1 className="text-3xl font-bold text-gray-900">Detalhes do Exame</h1>
            </div>
            <p className="text-gray-600">Visualize todas as informações do exame audiométrico</p>
          </div>

          {/* Main Content */}
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {/* Left Column - Main Info */}
            <div className="lg:col-span-2 space-y-6">
              {/* Patient Info Card */}
              <div className="bg-white rounded-lg shadow-md p-6">
                <h2 className="text-xl font-bold text-gray-900 mb-4 flex items-center gap-2">
                  <UserIcon className="w-6 h-6 text-blue-600" />
                  Informações do Paciente
                </h2>
                <div className="grid grid-cols-3 gap-4">
                  <div className="col-span-3">
                    <p className="text-sm text-gray-600 mb-1">Nome do Paciente</p>
                    <p className="text-base font-semibold text-gray-900">
                      {exam.patientName || 'N/A'}
                    </p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-600 mb-1">Sexo</p>
                    <p className="text-base font-medium text-gray-900">
                      {getSexLabel(exam.patientSex)}
                    </p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-600 mb-1">Idade</p>
                    <p className="text-base font-medium text-gray-900">
                      {exam.patientAge ? `${exam.patientAge} anos` : 'N/A'}
                    </p>
                  </div>
                </div>
              </div>

              {/* Exam Info Card */}
              <div className="bg-white rounded-lg shadow-md p-6">
                <h2 className="text-xl font-bold text-gray-900 mb-4 flex items-center gap-2">
                  <ClipboardDocumentCheckIcon className="w-6 h-6 text-green-600" />
                  Dados do Exame
                </h2>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <p className="text-sm text-gray-600 mb-1">Data do Exame</p>
                    <div className="flex items-center gap-2">
                      <CalendarIcon className="w-4 h-4 text-gray-400" />
                      <p className="text-base font-semibold text-gray-900">
                        {formatDate(exam.date)}
                      </p>
                    </div>
                  </div>
                  <div>
                    <p className="text-sm text-gray-600 mb-1">Operador</p>
                    <p className="text-base font-semibold text-gray-900">
                      {exam.operatorName || 'N/A'}
                    </p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-600 mb-1">Registro do Operador</p>
                    <p className="text-base font-medium text-gray-700">
                      {formatCRM(exam.operatorCrm, exam.operatorCrmState) || exam.operatorRegistration || '-'}
                    </p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-600 mb-1">Criado em</p>
                    <p className="text-base text-gray-700">
                      {formatDateTime(exam.createdAt)}
                    </p>
                  </div>
                </div>
              </div>

              {/* Audiogram Data Card */}
              {exam.audiogramDataId && (
                <div className="bg-white rounded-lg shadow-md p-6">
                  <h2 className="text-xl font-bold text-gray-900 mb-4">Dados Audiométricos</h2>
                  <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                    <p className="text-sm text-blue-800">
                      <span className="font-semibold">ID do Audiograma:</span>{' '}
                      <span className="font-mono">
                        {exam.audiogramDataId.substring(0, 8)}...
                      </span>
                    </p>
                    <p className="text-sm text-blue-700 mt-2">
                      ✓ Exame contém dados audiométricos completos
                    </p>
                  </div>
                </div>
              )}

              {/* Speech Test Data Card */}
              {exam.speechTestId && (
                <div className="bg-white rounded-lg shadow-md p-6">
                  <h2 className="text-xl font-bold text-gray-900 mb-4">Teste de Fala</h2>
                  <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                    <p className="text-sm text-green-800">
                      <span className="font-semibold">ID do Teste:</span>{' '}
                      <span className="font-mono">
                        {exam.speechTestId.substring(0, 8)}...
                      </span>
                    </p>
                    <p className="text-sm text-green-700 mt-2">
                      ✓ Exame contém dados de logoaudiometria
                    </p>
                  </div>
                </div>
              )}
            </div>

            {/* Right Column - Quick Info */}
            <div className="space-y-6">
              {/* Quick Stats Card */}
              <div className="bg-gradient-to-br from-blue-500 to-purple-600 rounded-lg shadow-lg p-6 text-white">
                <h3 className="text-lg font-bold mb-4">Resumo Rápido</h3>
                <div className="space-y-3">
                  <div className="flex items-center justify-between">
                    <span className="text-blue-100">Status:</span>
                    <span className="font-semibold">Completo</span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-blue-100">Audiograma:</span>
                    <span className="font-semibold">
                      {exam.audiogramDataId ? '✓ Sim' : '✗ Não'}
                    </span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-blue-100">Teste de Fala:</span>
                    <span className="font-semibold">
                      {exam.speechTestId ? '✓ Sim' : '✗ Não'}
                    </span>
                  </div>
                </div>
              </div>

              {/* Actions Card */}
              <div className="bg-white rounded-lg shadow-md p-6">
                <h3 className="text-lg font-bold text-gray-900 mb-4">Ações</h3>
                <div className="space-y-3">
                  <button
                    onClick={() => navigate(`/patients/${exam.patientId}`)}
                    className="w-full px-4 py-2 bg-blue-100 text-blue-700 rounded-lg hover:bg-blue-200 transition-colors font-medium"
                    disabled={!exam.patientId}
                  >
                    Ver Paciente
                  </button>
                  <button
                    onClick={() => {
                      if (examFiles.length > 0) {
                        openImageModal(examFiles[0].storageUrl);
                      } else {
                        alert('Nenhum arquivo de exame encontrado');
                      }
                    }}
                    className="w-full px-4 py-2 bg-indigo-100 text-indigo-700 rounded-lg hover:bg-indigo-200 transition-colors font-medium flex items-center justify-center gap-2"
                    disabled={examFiles.length === 0}
                  >
                    <PhotoIcon className="w-5 h-5" />
                    Ver Exame
                  </button>
                  <button
                    onClick={() => navigate(`/patients/${exam.patientId}/evolution`)}
                    className="w-full px-4 py-2 bg-purple-100 text-purple-700 rounded-lg hover:bg-purple-200 transition-colors font-medium"
                    disabled={!exam.patientId}
                  >
                    Ver Evolução
                  </button>
                  <button
                    onClick={() => navigate('/report-wizard')}
                    className="w-full px-4 py-2 bg-green-100 text-green-700 rounded-lg hover:bg-green-200 transition-colors font-medium"
                  >
                    Organizar Laudo
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Image Modal */}
      {showImageModal && selectedImage && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black bg-opacity-75 animate-fadeIn">
          <div className="relative max-w-6xl max-h-[90vh] w-full bg-white rounded-2xl shadow-2xl overflow-hidden animate-scaleIn">
            {/* Modal Header */}
            <div className="absolute top-0 left-0 right-0 bg-gradient-to-b from-black/70 to-transparent p-6 z-10">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <PhotoIcon className="w-8 h-8 text-white" />
                  <h3 className="text-xl font-bold text-white">
                    Exame Audiométrico
                  </h3>
                </div>
                <button
                  onClick={closeImageModal}
                  className="p-2 rounded-full bg-white/20 hover:bg-white/30 transition-colors backdrop-blur-sm"
                  title="Fechar"
                >
                  <XMarkIcon className="w-6 h-6 text-white" />
                </button>
              </div>
              {examFiles.length > 0 && (
                <p className="text-sm text-white/90 mt-2">
                  📄 {examFiles[0].fileName} • {(examFiles[0].fileSizeBytes / 1024).toFixed(0)} KB
                </p>
              )}
            </div>

            {/* Modal Content - Image */}
            <div className="flex items-center justify-center p-4 bg-gray-100">
              <img
                src={selectedImage}
                alt="Exame Audiométrico"
                className="max-w-full max-h-[80vh] object-contain rounded-lg shadow-lg"
                onError={(e) => {
                  e.target.onerror = null;
                  e.target.src = 'data:image/svg+xml,%3Csvg xmlns="http://www.w3.org/2000/svg" width="400" height="300"%3E%3Crect fill="%23ddd" width="400" height="300"/%3E%3Ctext fill="%23999" x="50%25" y="50%25" dominant-baseline="middle" text-anchor="middle"%3EImagem não disponível%3C/text%3E%3C/svg%3E';
                }}
              />
            </div>

            {/* Modal Footer */}
            <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/70 to-transparent p-6">
              <div className="flex items-center justify-between">
                <p className="text-sm text-white/80">
                  Clique fora da imagem ou no X para fechar
                </p>
                <a
                  href={selectedImage}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="px-4 py-2 bg-white/20 hover:bg-white/30 text-white rounded-lg transition-colors backdrop-blur-sm font-medium"
                >
                  Abrir em Nova Aba
                </a>
              </div>
            </div>
          </div>

          {/* Click outside to close */}
          <div
            className="absolute inset-0 -z-10"
            onClick={closeImageModal}
          />
        </div>
      )}
    </DashboardLayout>
  );
};

export default ExamDetails;
